<script setup lang="ts">
import { computed } from 'vue'

interface Project {
  id: number
  tag_id: number
  title: string
  description: string
  sort: number
  create_at: number
  thumb: string
  startup_type: string
  market_score: number
  product_score: number
  tech_score: number
  business_score: number
}

const props = defineProps<{
  projects: Project[]
}>()

const ratingLabels = {
  market_score: '市场可行性',
  product_score: '产品可行性',
  tech_score: '技术可行性',
  business_score: '商业模式',
}

const getStars = (count: number) => '★'.repeat(count) + '☆'.repeat(10 - count)

// 格式化日期
const formatDate = (timestamp: number) => {
  const date = new Date(timestamp * 1000)
  return date.toLocaleDateString('zh-CN')
}

// 跳转到项目详情页 (hash 模式)
const navigateToDetail = (id: number) => {
  window.location.hash = `/startup/item?id=${id}`
}
</script>

<template>
  <div class="project-list">
    <div
      v-for="project in props.projects"
      :key="project.id"
      class="project-card"
      :class="{ 'mvp-card': project.startup_type === 'MVP', 'pivot-card': project.startup_type === 'PIVOT' }"
      @click="navigateToDetail(project.id)"
    >
      <div v-if="project.startup_type === 'MVP'" class="mvp-badge">MVP</div>
      <div v-else-if="project.startup_type === 'PIVOT'" class="pivot-badge">PIVOT</div>
      <div class="project-header">
        <h3 class="project-name">{{ project.title }}</h3>
        <span class="project-date">{{ formatDate(project.create_at) }}</span>
      </div>
      <p class="project-desc">{{ project.description }}</p>
      <div class="project-ratings">
        <div v-for="(label, key) in ratingLabels" :key="key" class="rating-item">
          <span class="rating-label">{{ label }}</span>
          <span class="rating-stars">{{ getStars(project[key as keyof Project] as number) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.project-list {
  padding: 1rem 1.5rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.project-card {
  background: #fff;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s, box-shadow 0.3s;
  cursor: pointer;
}

.project-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.project-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.project-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
}

.project-date {
  font-size: 0.75rem;
  padding: 0.2rem 0.5rem;
  background: #f0f0f0;
  color: #666;
  border-radius: 4px;
}

.project-desc {
  font-size: 0.85rem;
  color: #666;
  line-height: 1.5;
  margin: 0 0 1rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.project-ratings {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.rating-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
}

.rating-label {
  color: #666;
}

.rating-stars {
  color: #ffc107;
  font-size: 0.9rem;
  letter-spacing: 2px;
}

/* MVP 项目样式 */
.mvp-card {
  background: #e8f5e9 !important;
  border: 2px solid #42b883;
  position: relative;
  overflow: hidden;
}

.mvp-badge {
  position: absolute;
  top: 8px;
  right: -28px;
  background: #42b883;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 2rem;
  transform: rotate(45deg);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* PIVOT 项目样式 */
.pivot-card {
  background: #f5f5f5 !important;
  border: 2px solid #999;
  position: relative;
  overflow: hidden;
  opacity: 0.8;
}

.pivot-badge {
  position: absolute;
  top: 8px;
  right: -28px;
  background: #999;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 2rem;
  transform: rotate(45deg);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}
</style>
